/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import type { ErrorMessage } from '../types/error'
import { getUserIdToStorage } from '../utils/miiaFwCognito'
import { useApiGw } from './useApiGw'
import { useToken } from './useToken'
import MiiaFwConfig from '@/configs/config'

export const useUserAttributes = () => {
  const miiaFwStore = useMiiaFwStore()
  const { getTokens } = useToken()
  const { invokeApiGwFunc } = useApiGw()

  // トークンからユーザ属性を取得する。
  const getUserAttributesFromStore = async (): Promise<any> => {
    const miiaFwStore = useMiiaFwStore()
    const { attributes } = storeToRefs(miiaFwStore)
    return new Promise((resolve, reject) => {
      if (attributes.value !== undefined) {
        resolve(attributes.value)
      } else {
        const unwatch = watch(attributes, (_newVal, oldVal) => {
          if (oldVal === undefined) {
            resolve(attributes.value)
          } else {
            // 処理なし
          }
          unwatch() // 監視を解除
          clearTimeout(timerObj)
        })
        // 5秒以内にトークンからユーザ属性を取得できなかった場合はタイムアウト
        const timerObj = setTimeout(() => {
          unwatch()
          reject({ message: 'ユーザ属性の取得に失敗しました。' })
        }, 5000)
      }
    })
  }

  const getAttributes = async (): Promise<object | ErrorMessage> => {
    return new Promise((resolve, reject) => {
      const userId = getUserIdToStorage()
      if (userId === '') {
        return reject({ message: 'ユーザIDを取得できませんでした。' })
      }
      const tokens = getTokens()
      if (tokens === null || !Object.prototype.hasOwnProperty.call(tokens, 'provider')) {
        return reject({ message: 'サインアウト中です。' })
      }
      if (tokens.provider.toLowerCase() === 'cognito') {
        invokeApiGwFunc(
          MiiaFwConfig.config.authEndpoint,
          // Lambda関数をコールするためのWebAPIのサブディレクトリ
          '/miiafw-user-m-dao',
          // Lambda関数に渡す引数
          { function: 'get', params: { user_id: userId, system: MiiaFwConfig.config.sysId } },
          // Lambda関数実行前の前処理のコールバック
          () => {},
          // 成功時のコールバック
          (data: any) => {
            updateAttributes(data.result)
            resolve(data.result)
          },
          // 例外受信時のコールバック(引数: エラーメッセージ)
          (message: any) => {
            updateAttributes({})
            reject({ message: message })
          },
          // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
          () => {
            updateAttributes({})
            reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
          },
          // 処理完了後に実行するコールバック
          () => {}
        )
      } else {
        reject({ message: '対応していないIDプロバイダが設定されました。' })
      }
    })
  }

  /**
   * Vuexにストアされている'miiaFwStore/updateAttributes'を更新する。
   * 属性情報を更新するときに使用する。
   * @param attributes 認可情報
   */
  const updateAttributes = (attributes: { [key: string]: string }): void => {
    miiaFwStore.updateAttributes(attributes)
  }

  return {
    getUserAttributesFromStore,
    getAttributes
  }
}
